import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface MarkDownFormatterProps {
  content: string;
  componentOverrides?: Partial<Components>;
}

export function MarkDownFormatter({ content, componentOverrides = {} }: MarkDownFormatterProps) {
  const defaultComponents: Components = {
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="list-disc mb-2">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal mb-2">{children}</ol>,
    li: ({ children }) => <li className="ml-4 mb-1">{children}</li>,
    code: ({ children, className }) => {
      const isBlock = className?.includes('language-');
      return isBlock ? (
        <pre className="bg-gray-100 p-4 rounded-lg mb-4 overflow-x-auto">
          <code className={className}>{children}</code>
        </pre>
      ) : (
        <code className="bg-gray-100 text-pink-600 px-1.5 py-0.5 rounded text-xs font-mono">
          {children}
        </code>
      );
    },
    h1: ({ children }) => <h1 className="text-lg font-semibold mb-2 mt-1">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-semibold mb-2 mt-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold mb-1 mt-1">{children}</h3>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-2 text-gray-700">
        {children}
      </blockquote>
    ),
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    a: ({ children, href }) => (
      <a
        href={href}
        className="text-blue-600 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-gray-200">{children}</tbody>,
    tr: ({ children }) => <tr className="hover:bg-gray-50">{children}</tr>,
    th: ({ children }) => (
      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200">{children}</td>
    ),
  };

  // Merge default components with any overrides
  const components = { ...defaultComponents, ...componentOverrides };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
}
